import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { useCollectionState } from "../lib/useCollection";
import { resolveMediaUrl } from "../lib/mediaUrl";
import { buildWhatsAppLink } from "../lib/constants";

const PortfolioDetailPage = () => {
    const { id } = useParams();
    const { items, loading } = useCollectionState("/portfolio");
    const item = useMemo(() => items.find((i) => i.id === id), [items, id]);

    useDocumentMeta({
        title: item
            ? `${item.title} — ${item.category} Project | Raj Graphics Nagpur`
            : "Project — Raj Graphics",
        description: item
            ? `${item.title}: ${item.category} project by Raj Graphics, a design, branding and printing studio in Nagpur.`
            : "Project case study from Raj Graphics.",
        canonical: `/portfolio/${id}`,
    });

    const related = useMemo(
        () => items.filter((i) => i.id !== id && i.category === item?.category).slice(0, 4),
        [items, id, item],
    );

    if (!item) {
        return (
            <main data-testid="portfolio-detail-page" className="bg-[#050505] min-h-screen pt-44 pb-32">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    {loading ? (
                        <h1 className="font-poppins font-semibold text-white text-3xl" data-testid="detail-loading">
                            Loading project…
                        </h1>
                    ) : (
                        <>
                            <h1 className="font-poppins font-semibold text-white text-3xl" data-testid="detail-not-found">
                                Project not found.
                            </h1>
                            <p className="mt-4 font-inter text-zinc-400">
                                This project may have been moved or removed — the rest of our work is right here.
                            </p>
                        </>
                    )}
                    <Link
                        to="/portfolio"
                        data-testid="detail-back-link"
                        className="mt-8 inline-flex items-center gap-2 text-gold font-inter hover:underline underline-offset-4"
                    >
                        <ArrowLeft size={16} /> Back to all work
                    </Link>
                </div>
            </main>
        );
    }

    const waLink = buildWhatsAppLink(
        `Hi Raj Graphics! I saw your work on "${item.title}" and would like to discuss a similar project.`,
    );

    return (
        <main data-testid="portfolio-detail-page" className="bg-[#050505]">
            <section className="relative pt-32 md:pt-40 pb-8">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <Link
                        to="/portfolio"
                        data-testid="detail-back-link"
                        className="inline-flex items-center gap-2 font-inter text-sm text-zinc-400 hover:text-gold transition-colors mb-8"
                    >
                        <ArrowLeft size={15} /> All work
                    </Link>
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                        {item.category}
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                        data-testid="detail-title"
                    >
                        {item.title}
                    </motion.h1>

                    {(item.client || item.industry) && (
                        <div className="mt-8 flex flex-wrap gap-x-12 gap-y-4" data-testid="detail-meta">
                            {item.client && (
                                <div>
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Client</div>
                                    <div className="font-poppins text-white">{item.client}</div>
                                </div>
                            )}
                            {item.industry && (
                                <div>
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Industry</div>
                                    <div className="font-poppins text-white">{item.industry}</div>
                                </div>
                            )}
                            <div>
                                <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Services</div>
                                <div className="font-poppins text-white">{item.category}</div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="relative pb-12">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="rounded-2xl overflow-hidden border border-white/5 bg-[#0a0a0a]"
                    >
                        <img
                            src={resolveMediaUrl(item.image_url)}
                            alt={`${item.title} — ${item.category} by Raj Graphics`}
                            className="w-full h-auto max-h-[80vh] object-cover"
                            data-testid="detail-image"
                        />
                    </motion.div>

                    {item.description && (
                        <div className="mt-10 max-w-3xl" data-testid="detail-description">
                            <h2 className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                                About this project
                            </h2>
                            <p className="font-inter text-base md:text-lg text-zinc-300 leading-relaxed whitespace-pre-line">
                                {item.description}
                            </p>
                        </div>
                    )}

                    <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="detail-whatsapp-cta"
                        className="mt-10 group inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-8 py-3.5 rounded-full transition-all hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                    >
                        Discuss a similar project
                        <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                    </a>
                </div>
            </section>

            {related.length > 0 && (
                <section className="relative py-16 border-t border-white/5" data-testid="detail-related">
                    <div className="max-w-6xl mx-auto px-6 md:px-12">
                        <h2 className="font-poppins font-semibold text-white text-2xl md:text-3xl mb-8">
                            More {item.category} work
                        </h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {related.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/portfolio/${r.id}`}
                                    data-testid={`related-item-${r.id}`}
                                    className="group relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-gold/30 transition-colors aspect-[4/3]"
                                >
                                    <img
                                        src={resolveMediaUrl(r.image_url)}
                                        alt={r.title}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                                        <span className="font-poppins text-white text-sm flex items-center gap-1.5">
                                            {r.title} <ArrowUpRight size={13} className="text-gold" />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <FooterCTA />
        </main>
    );
};

export default PortfolioDetailPage;
